if (!customElements.get('text-navigation')) {
  customElements.define('text-navigation', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.root = this.querySelector('.text-navigation__root');
      this.categories = [...this.querySelectorAll('.text-navigation__category')];
      this.desktop = window.matchMedia('(min-width: 769px)');
      this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.setAttribute('data-enhanced', '');
      this.querySelectorAll('.text-navigation__trigger, .text-navigation__page-link').forEach((trigger) => {
        trigger.addEventListener('pointerenter', () => {
          if (!this.desktop.matches || !this.hover.matches) return;
          this.schedule(() => this.openRoot());
        }, { signal });
      });
      this.addEventListener('pointerleave', () => {
        clearTimeout(this.timer);
        if (this.desktop.matches && this.hover.matches && !this.hasKeyboardFocus(this)) {
          this.schedule(() => this.closeRoot(), 250);
        }
      }, { signal });
      this.addEventListener('pointerenter', () => clearTimeout(this.timer), { signal });
      this.addEventListener('click', (event) => {
        event.stopPropagation();
        const trigger = event.target.closest('summary');
        if (!trigger) return;
        clearTimeout(this.timer);
        if (trigger.parentElement === this.root && !this.root.open) this.closeOtherMenus();
      }, { signal });
      this.categories.forEach((category) => {
        category.querySelector('summary').addEventListener('pointerenter', () => {
          if (!this.desktop.matches || !this.hover.matches) return;
          this.schedule(() => {
            if (this.categories.some((other) => other !== category && other.open && this.hasKeyboardFocus(other))) return;
            this.categories.forEach((other) => { other.open = other === category; });
            this.measure();
          });
        }, { signal });
        category.addEventListener('pointerleave', () => clearTimeout(this.timer), { signal });
        category.addEventListener('toggle', () => {
          if (category.open) {
            this.categories.forEach((other) => { if (other !== category) other.open = false; });
          } else {
            this.closeSubcategories(category);
          }
          this.measure();
        }, { signal });
      });
      this.querySelectorAll('.text-navigation__subcategory').forEach((subcategory) => {
        subcategory.querySelector('summary').addEventListener('pointerenter', () => {
          if (!this.hasAttribute('data-side-panel-ready') || !this.hover.matches) return;
          this.schedule(() => {
            const siblings = subcategory.closest('.text-navigation__panel').querySelectorAll('.text-navigation__subcategory');
            if ([...siblings].some((other) => other !== subcategory && other.open && this.hasKeyboardFocus(other))) return;
            subcategory.open = true;
          });
        }, { signal });
        subcategory.addEventListener('pointerleave', () => clearTimeout(this.timer), { signal });
        subcategory.addEventListener('toggle', () => {
          if (subcategory.open) {
            subcategory.closest('.text-navigation__panel').querySelectorAll('.text-navigation__subcategory').forEach((other) => {
              if (other !== subcategory) other.open = false;
            });
          }
          this.measure();
        }, { signal });
      });
      this.querySelectorAll('.text-navigation__categories > li > a, .text-navigation__panel > .text-navigation__overview, .text-navigation__category-title > a, .text-navigation__panel > ul > li > a').forEach((link) => {
        link.addEventListener('pointerenter', () => {
          if (!this.desktop.matches || !this.hover.matches) return;
          const panel = link.closest('.text-navigation__panel');
          if (panel && !this.hasAttribute('data-side-panel-ready')) return;
          this.schedule(() => {
            const disclosures = panel
              ? [...panel.querySelectorAll('.text-navigation__subcategory')]
              : this.categories;
            if (disclosures.some((disclosure) => disclosure.open && this.hasKeyboardFocus(disclosure))) return;
            if (panel) this.closeSubcategories(panel);
            else this.resetDisclosures();
            this.measure();
          });
        }, { signal });
        link.addEventListener('pointerleave', () => clearTimeout(this.timer), { signal });
      });
      this.closest('.main-menu__disclosure')?.addEventListener('toggle', (event) => {
        if (!event.target.open) this.closeRoot();
      }, { signal });
      this.root.addEventListener('toggle', () => {
        if (!this.root.open) this.resetDisclosures();
        this.measure();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.root.open) return;
        event.preventDefault();
        event.stopPropagation();
        const disclosure = event.target.closest('.text-navigation__subcategory[open], .text-navigation__category[open]');
        if (disclosure) {
          disclosure.open = false;
          this.closeSubcategories(disclosure);
          disclosure.querySelector('summary').focus();
        } else {
          this.closeRoot();
          this.root.querySelector('summary').focus();
        }
      }, { signal });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') event.stopPropagation();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.desktop.matches && event.relatedTarget && !this.contains(event.relatedTarget)) this.closeRoot();
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.closeRoot();
      }, { signal });
      this.desktop.addEventListener('change', () => this.closeRoot(), { signal });
      window.addEventListener('resize', () => this.measure(), { signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (event.target === this || this.contains(event.target)) this.openRoot();
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (event.target === this || this.contains(event.target)) this.closeRoot();
      }, { signal });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => {
          cancelAnimationFrame(this.measureFrame);
          this.measureFrame = requestAnimationFrame(() => this.measure());
        });
        this.querySelectorAll('.text-navigation__panel, .text-navigation__submenu').forEach((panel) => this.observer.observe(panel));
      }
    }

    disconnectedCallback() {
      this.controller.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.measureFrame);
      clearTimeout(this.timer);
      this.removeAttribute('data-enhanced');
      this.removeAttribute('data-side-panel-ready');
      this.removeAttribute('data-side-panel-open');
    }

    hasKeyboardFocus(container) {
      const active = document.activeElement;
      return container.contains(active) && active.matches(':focus-visible');
    }

    schedule(callback, delay = 160) {
      clearTimeout(this.timer);
      this.timer = setTimeout(callback, delay);
    }

    closeOtherMenus() {
      if (!this.desktop.matches) return;
      const menu = this.closest('main-menu');
      if (menu?.querySelector('.main-nav details.is-open')) menu.handleClose();
      document.querySelectorAll('text-navigation').forEach((other) => {
        if (other !== this) other.closeRoot();
      });
    }

    openRoot() {
      this.closeOtherMenus();
      this.root.open = true;
    }

    closeSubcategories(container) {
      container.querySelectorAll('.text-navigation__subcategory').forEach((subcategory) => { subcategory.open = false; });
    }

    resetDisclosures() {
      this.categories.forEach((category) => { category.open = false; });
      this.closeSubcategories(this);
    }

    closeRoot() {
      clearTimeout(this.timer);
      this.root.open = false;
      this.resetDisclosures();
      this.removeAttribute('data-side-panel-ready');
      this.removeAttribute('data-side-panel-open');
    }

    measure() {
      if (!this.root.open || !this.desktop.matches) return;
      const width = document.documentElement.clientWidth;
      const styles = getComputedStyle(this);
      const baseWidth = parseFloat(styles.getPropertyValue('--text-navigation-width')) || 720;
      const sideWidth = parseFloat(styles.getPropertyValue('--navigation-side-width'));
      const dropdown = this.querySelector('.text-navigation__dropdown');
      const dropdownStyles = getComputedStyle(dropdown);
      const borders = parseFloat(dropdownStyles.borderLeftWidth) + parseFloat(dropdownStyles.borderRightWidth);
      const scrollbarWidth = Math.max(0, dropdown.offsetWidth - dropdown.clientWidth - borders);
      this.style.setProperty('--navigation-scrollbar-width', `${scrollbarWidth}px`);
      const sideReady = width >= baseWidth + sideWidth + scrollbarWidth + 32;
      this.toggleAttribute('data-side-panel-ready', sideReady);
      const panel = this.querySelector('.text-navigation__category[open] > .text-navigation__panel');
      const submenu = panel?.querySelector('.text-navigation__subcategory[open] > .text-navigation__submenu');
      this.toggleAttribute('data-side-panel-open', sideReady && !!submenu);
      const height = Math.max(panel?.getBoundingClientRect().height || 0, sideReady ? submenu?.getBoundingClientRect().height || 0 : 0);
      this.style.setProperty('--navigation-panel-height', `${Math.ceil(height)}px`);
      this.style.setProperty('--navigation-shift', '0px');
      const bounds = dropdown.getBoundingClientRect();
      this.style.setProperty('--navigation-available-height', `${Math.max(44, window.innerHeight - bounds.top - 16)}px`);
      const shift = bounds.right > width - 16 ? width - 16 - bounds.right : Math.max(0, 16 - bounds.left);
      this.style.setProperty('--navigation-shift', `${shift}px`);
    }
  });
}
